import { MetricEventName } from '../types/metric-events';
import { emitMetric } from './metric-emit';

/**
 * Core Web Vitals from real page loads, sampled.
 *
 * We already run `core-web-vitals-validator.js` — synthetic Lighthouse passes
 * on a fast machine. Those cannot see what a phone on a slow network actually
 * experiences, and after the May 2026 core update that is the number that
 * ranks. This reports the field values through the same `frontend-events`
 * pipeline as the rest of the funnel, so a vitals regression and the conversion
 * drop it causes are queryable in one place.
 */

/**
 * Decision (2026-09-02): 10 %, decided ONCE per page load.
 *
 * Four vitals on every load would add ~4 events per load against a baseline of
 * 66k events/day and roughly double the collection — for a signal that is a
 * distribution, not a count. At 10 % a page with a few hundred loads a day
 * still yields enough samples for a p75, which is what the Core Web Vitals
 * thresholds are defined on. Sampling per LOAD rather than per METRIC keeps a
 * sampled session's four vitals together, so LCP and CLS can be correlated
 * within one visit instead of only in aggregate.
 */
export const WEB_VITALS_SAMPLE_RATE = 0.1;

/** Guard against a double init (React StrictMode double-mounts in dev). */
let initialized = false;

/** The four vitals we report. FCP is omitted — LCP covers the same question. */
export type WebVitalName = 'LCP' | 'INP' | 'CLS' | 'TTFB';

export interface WebVitalsAttributes {
    metric: WebVitalName;
    value: number;
    rating: string;
    page: string;
    navType: string;
}

/** True when this page load is in the sample. */
export function shouldSampleWebVitals(
    random: number = Math.random(),
    rate: number = WEB_VITALS_SAMPLE_RATE,
): boolean {
    return random < rate;
}

/**
 * Bounded attributes for one vital.
 *
 * CLS is unitless and typically 0.0-0.3, so it is multiplied by 1000 and
 * rounded — an integer the same way the other three are integers in ms, and
 * without it every CLS would round to 0. Everything else is rounded to whole
 * milliseconds: sub-millisecond precision is noise and would make the value
 * effectively unique per event.
 */
export function toWebVitalsAttributes(
    metric: { name: string, value: number, rating?: string, navigationType?: string },
    page: string,
): WebVitalsAttributes {
    const name = metric.name as WebVitalName;
    const value = name === 'CLS'
        ? Math.round((metric.value || 0) * 1000)
        : Math.round(metric.value || 0);
    return {
        metric: name,
        value,
        rating: metric.rating || 'unknown',
        page,
        navType: metric.navigationType || 'unknown',
    };
}

/**
 * Register the vitals listeners for this page load, if it is in the sample.
 *
 * No-op without a `window` (SSG/SSR export runs this file's module graph but
 * must never touch a browser API), and no-op on a second call.
 *
 * `web-vitals` is loaded dynamically AFTER the sampling decision, so 90 % of
 * loads never fetch the library at all. `reportAllChanges: false` means each
 * vital reports once, when it is final — CLS and INP on visibility change,
 * which the existing `pagehide`/`beforeunload` flush in _app.tsx then ships
 * with whatever else is in the buffer.
 */
export type WebVitalsReporter = (cb: (metric: any) => void, opts: { reportAllChanges: boolean }) => void;
export interface WebVitalsModule {
    onLCP: WebVitalsReporter;
    onINP: WebVitalsReporter;
    onCLS: WebVitalsReporter;
    onTTFB: WebVitalsReporter;
}

/** Injectable so a test can drive the listeners without the real library. */
const defaultLoader = (): Promise<WebVitalsModule> => import('web-vitals') as any;

export function initWebVitals(
    sampled: boolean = shouldSampleWebVitals(),
    load: () => Promise<WebVitalsModule> = defaultLoader,
): void {
    if (typeof window === 'undefined') return;
    if (initialized) return;
    initialized = true;
    if (!sampled) return;

    const page = window.location.pathname;
    const report = (metric: any) => {
        try {
            emitMetric(MetricEventName.WEB_VITALS, toWebVitalsAttributes(metric, page) as any);
        } catch { /* telemetry is never load-bearing */ }
    };

    /**
     * ONE OPTIONS OBJECT PER REGISTRATION — NEVER ONE SHARED BETWEEN THEM (2026-09-05).
     *
     * `web-vitals` v5 keys each metric's internal manager off the IDENTITY of the options object
     * it is handed: `dist/modules/lib/initUnique.js` holds `const instanceMap = new WeakMap()`
     * and `initUnique(opts, ClassObj)` returns `instanceMap.get(opts)` if that exact object has
     * been seen before. `onLCP` asks it for an `LCPEntryManager`, `onINP` for an
     * `InteractionManager` and `onCLS` for a `LayoutShiftManager` — so with ONE object shared
     * between them the first caller populates the map and the other two are handed the FIRST
     * caller's manager instead of their own.
     *
     * Measured on this page: `onLCP` ran first, so the INP observer's
     * `interactionManager._estimateP98LongestInteraction()` was called on an `LCPEntryManager`
     * and threw `TypeError: l.L is not a function` (minified) on EVERY pointer interaction, on
     * every page of the site — INP was therefore never reported at all, and CLS, whose
     * `layoutShiftManager._sessionValue` was likewise undefined on the wrong instance, never
     * cleared `undefined > metric.value` and never reported either.
     *
     * A fresh object per call is the whole fix: the WeakMap gets four distinct keys and each
     * metric gets the manager class it asked for. The VALUE is identical for all four, which is
     * what made one shared literal look like a tidy-up in the first place.
     */
    load().then(({ onCLS, onINP, onLCP, onTTFB }) => {
        onLCP(report, { reportAllChanges: false });
        onINP(report, { reportAllChanges: false });
        onCLS(report, { reportAllChanges: false });
        onTTFB(report, { reportAllChanges: false });
    }).catch(() => { /* a missing chunk must never break the page */ });
}

/** Test seam: forget that init already ran. */
export function resetWebVitalsForTest(): void {
    initialized = false;
}
